import React, { useState, useEffect, useMemo } from "react";
import Form from "@rjsf/mui";
import validator from "@rjsf/validator-ajv8";
import { groupInfoSchema, cobraAdminSchema, productsSoldSchema, enrollmentSchema, docUploadSchema } from "./formSchema";
import {
  groupInfoUiSchema,
  cobraAdminUiSchema,
  productsSoldUiSchema,
  enrollmentUiSchema,
  docUploadUiSchema,
} from "./uiSchema";
import { buildUiSchemaWithShrunkLabels } from "../utils/uiSchemaHelper";
import PhoneWidget from "../widgets/PhoneWidget";
import NumericWidget from "../widgets/NumericWidget";
import TaxIdWidget from "../widgets/TaxIdWidget";
import ObjectFieldTemplate from "../templates/ObjectFieldTemplate";
import TitleFieldTemplate from "../templates/TitleFieldTemplate";
import GIFTemplate from "../templates/GIFTemplate";
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  ThemeProvider,
  createTheme,
  CssBaseline,
  CircularProgress,
} from "@mui/material";
import { IChangeEvent } from "@rjsf/core";
import styles from "../form.module.css";
import img from "../assets/657507_tmpl_head_60db3e2024ea6.png";
import { GifFormData } from "./gifModels";
import { host } from "config";
import { convertJSONToMultiPartFormData } from "../utils/general";
import FormSubmittedSplashPage from "../formSubmittedSplashPage";
import SaveAndResumeDialog from "../saveAndResume/saveAndResumeDialog";
import ConfirmResumeOTCDialog from "../saveAndResume/confirmResumeOTC";
import { useParams } from "react-router-dom";

// Create MUI theme matching the form style
const theme = createTheme({
  palette: {
    primary: {
      main: "#2196f3",
    },
    background: {
      default: "#ffffff",
    },
  },
  typography: {
    fontFamily: '"Open Sans", Arial, sans-serif',
  },
  components: {
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        size: "small",
        InputLabelProps: {
          shrink: true,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
        },
      },
    },
    MuiFormGroup: {
      styleOverrides: {
        root: {
          alignItems: "flex-start",
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-start",
          marginLeft: 0,
          width: "100%",
          marginTop: 0,
          marginBottom: 0,
        },
        label: {
          marginLeft: "8px", // spacing between radio and text
          textAlign: "left",
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        MenuProps: {
          PaperProps: {
            style: {
              maxHeight: 300,
              maxWidth: 300,
            },
          },
        },
      },
      styleOverrides: {
        select: {
          width: "700px",
          boxSizing: "border-box",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          display: "block",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          alignItems: "flex-start",
          paddingTop: 8,
          paddingBottom: 8,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          width: "100%",
        },
        input: {
          minWidth: 0,
          overflow: "hidden",
        },
      },
    },
  },
});

// Custom widgets
const widgets = {
  PhoneWidget: PhoneWidget,
  NumericWidget: NumericWidget,
  TaxIdWidget: TaxIdWidget,
};

const formNumber = "1";

const sensitiveKeys = ["locationTemplate", "taxAndWageReport"];

const fileFields = ["locationTemplate", "taxAndWageReport"];

// Custom templates
const templates = {
  GIFTemplate: GIFTemplate,
};

const GroupImplementationForm: React.FC = () => {
  const [strippedFormData, setStrippedFormData] = useState({});
  const [formData, setFormData] = useState<GifFormData | undefined>();
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSubmittedPage, setShowSubmittedPage] = useState(false);
  const [openSaveAndResume, setOpenSaveAndResume] = useState(false);
  const [openConfirmResumeOTC, setOpenConfirmResumeOTC] = useState(false);

  const { guid } = useParams();

  useEffect(() => {
    if (guid && guid != "") {
      setOpenConfirmResumeOTC(true);
    }
  }, [guid]);

  // Dynamically create UI schema based on form data
  const dynamicGiUiSchema = useMemo(() => {
    const baseGiUiSchema = { ...groupInfoUiSchema };

    // Show Broker fields only for Broker Commission Payment Setup
    if (formData?.isSameBillingAddress === "No") {
      baseGiUiSchema.companyBillingAddress1 = {};
      baseGiUiSchema.companyBillingCity = {};
      baseGiUiSchema.companyBillingState = {};
      baseGiUiSchema.companyBillingZip = { "ui:widget": "NumericWidget" };
    }

    if (formData?.hasTPA === "Yes") {
      baseGiUiSchema.nameOfTPA = {};
      baseGiUiSchema.emailOfTPA = { "ui:placeholder": "tpaemail@example.com" };
    }

    if (formData?.policySignerAsPortalContact === "No") {
      baseGiUiSchema.portalContactName = {};
      baseGiUiSchema.portalContactTitle = {};
      baseGiUiSchema.portalContactEmail = { "ui:placeholder": "portalcontact@example.com" };
      baseGiUiSchema.portalContactPhone = { "ui:widget": "PhoneWidget" };
    }

    if (formData?.addPrimaryContact === "Yes") {
      baseGiUiSchema.additionalContactName = {};
      baseGiUiSchema.additionalContactTitle = {};
      baseGiUiSchema.additionalContactEmail = { "ui:placeholder": "additionalcontact@example.com" };
      baseGiUiSchema.additionalContactPhone = { "ui:widget": "PhoneWidget" };
    }

    if (formData?.billingContact === "Need to add billing contact") {
      baseGiUiSchema.billingContactName = {};
      baseGiUiSchema.billingContactTitle = {};
      baseGiUiSchema.billingContactEmail = { "ui:placeholder": "billingcontact@example.com" };
      baseGiUiSchema.billingContactPhone = { "ui:widget": "PhoneWidget" };
    }

    if (formData?.hasMultipleLocations === "Yes") {
      baseGiUiSchema.numberOfLocations = { "ui:widget": "NumericWidget" };
      baseGiUiSchema.billingPreference = {};
      baseGiUiSchema.locationTemplate = {};
    }

    return buildUiSchemaWithShrunkLabels(groupInfoSchema, baseGiUiSchema);
  }, [formData]);

  const dynamicCaUiSchema = useMemo(() => {
    const baseCaUiSchema = { ...cobraAdminUiSchema };

    if (formData?.sendCobra === "No, the group has a COBRA administrator already") {
      baseCaUiSchema.cobraAdminName = {};
      baseCaUiSchema.cobraAdminEmail = { "ui:placeholder": "cobraadmin@example.com" };
      baseCaUiSchema.cobraAdminAddress = {};
      baseCaUiSchema.cobraAdminCity = {};
      baseCaUiSchema.cobraAdminState = {};
      baseCaUiSchema.cobraAdminZip = { "ui:widget": "NumericWidget" };
    }

    return buildUiSchemaWithShrunkLabels(cobraAdminSchema, baseCaUiSchema);
  }, [formData]);

  const dynamicPsUiSchema = useMemo(() => {
    const basePsUiSchema = { ...productsSoldUiSchema };

    if (formData?.productsSold?.includes("GAP")) {
      basePsUiSchema.majorMedReset = {};
      basePsUiSchema.isReplacingExistingGAP = {};
    }

    if (formData?.isReplacingExistingGAP === "Yes") {
      basePsUiSchema.currentCarrier = {};
      basePsUiSchema.currentGAPCoverageType = {};
      basePsUiSchema.currentGAPAddedBenefits = {};
    }

    if (formData?.productsSold?.includes("Limited Medical")) {
      basePsUiSchema.ltdMedOrHospitalIndemnity = {};
      basePsUiSchema.hasDisabled = {};
      basePsUiSchema.premiumsCollectedByPayrollDeduction = {};
    }

    if (formData?.ltdMedOrHospitalIndemnity === "Yes") {
      basePsUiSchema.optiMedReplace = {};
      basePsUiSchema.currentInsurer = {};
    }

    if (formData?.hasDisabled === "Yes") {
      basePsUiSchema.numberOfDisabledMembers = { "ui:widget": "NumericWidget" };
      basePsUiSchema.numberOfDisabledDependents = { "ui:widget": "NumericWidget" };
    }

    if (formData?.premiumsCollectedByPayrollDeduction === "Yes") {
      basePsUiSchema.firstPayrollDeductionDate = {};
      basePsUiSchema.deductionsPerYear = {};
      basePsUiSchema.payPeriodsPerYear = {};
      basePsUiSchema.deductionMonths = { "ui:widget": "CheckboxesWidget" };
      basePsUiSchema.remittanceFrequency = {};
    }

    if (formData?.productsSold?.includes("Level Funded - Major Medical")) {
      basePsUiSchema.taxAndWageReport = {};
    }

    return buildUiSchemaWithShrunkLabels(productsSoldSchema, basePsUiSchema);
  }, [formData]);

  const dynamicEpUiSchema = useMemo(() => {
    const baseEpUiSchema = { ...enrollmentUiSchema };
    if (
      formData?.enrollmentPreference ===
      "The group would like to establish and EDI or file feed connection from their payroll system"
    ) {
      baseEpUiSchema.ediContactName = {};
      baseEpUiSchema.ediContactEmail = { "ui:placeholder": "edicontact@example.com" };
    }

    if (
      formData?.enrollmentPreference ===
      "The group would like to download an OptiMedHealth Template and utilize that to submit their enrollment details"
    ) {
      // Click here template download
    }

    if (formData?.coverRetirees === "Yes") {
      baseEpUiSchema.includeEarlyRetirees = {};
      baseEpUiSchema.numberOfRetirees = { "ui:widget": "NumericWidget" };
    }

    if (formData?.idCardDistributionMethod === "Mailed to corporate address for company distribution") {
      baseEpUiSchema.isCorporateMailingSame = {};
    }

    if (formData?.isCorporateMailingSame === "No") {
      baseEpUiSchema.cardMailingAddress = {};
      baseEpUiSchema.cardMailingCity = {};
      baseEpUiSchema.cardMailingState = {};
      baseEpUiSchema.cardMailingZip = { "ui:widget": "NumericWidget" };
    }

    // Apply shrunk labels to all fields (forces labels to stay above inputs)
    return buildUiSchemaWithShrunkLabels(enrollmentSchema, baseEpUiSchema);
  }, [formData]);

  const getHiddenFields = (dynamicUiSchema) => {
    return Object.keys(dynamicUiSchema).filter((key) => dynamicUiSchema[key]["ui:widget"] === "hidden");
  };

  const getAllHiddenFields = (...uiSchemas) => {
    const allHidden = new Set();

    uiSchemas.forEach((schema) => {
      const hidden = getHiddenFields(schema);
      hidden.forEach((field) => allHidden.add(field));
    });

    return Array.from(allHidden);
  };

  const stripSensitiveFields = (formData: { [key: string]: any }) => {
    const strippedFormData = { ...formData };
    sensitiveKeys.map((key) => {
      delete strippedFormData[key];
    });
    setStrippedFormData(strippedFormData);
  };

  const handleOenSaveAndResume = () => {
    console.log(formData);
    stripSensitiveFields(formData);
    setOpenSaveAndResume(true);
  };

  const handleCloseSaveAndResume = () => {
    setOpenSaveAndResume(false);
  };

  const handleCloseConfirmResumeOTC = () => {
    setOpenConfirmResumeOTC(false);
  };

  const handleSubmit = (data: IChangeEvent<GifFormData>) => {
    const hiddenFields = getAllHiddenFields(dynamicGiUiSchema, dynamicCaUiSchema, dynamicPsUiSchema, dynamicEpUiSchema);

    const cleanedData = { ...formData };

    hiddenFields.forEach((path) => {
      const keys = (path as string).split(".");
      let obj = cleanedData;

      while (keys.length > 1) {
        const key = keys.shift();
        if (!obj[key]) return;
        obj = obj[key];
      }

      delete obj[keys[0]];
    });

    setIsLoading(true);
    fetch(`${host}/api/implementation/forms/gif`, {
      method: "POST",
      body: convertJSONToMultiPartFormData(cleanedData, fileFields),
    })
      .then((response) => {
        if (!response.ok) {
          response.text().then((text) => {
            throw new Error(text);
          });
        }
        response.json().then(async (data: any) => {
          setIsLoading(false);

          setShowSubmittedPage(true);
        });
      })
      .catch((error: any) => {
        console.error("Error:", error);
        alert(
          "There was an error while submitting the form. Please try again later and contact IT if the problem persists."
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleChange = (data: IChangeEvent<GifFormData>) => {
    setFormData(data.formData);
  };

  const handleError = (errors) => {
    console.log("Form errors:", errors);
  };

  const [page, setPage] = useState(0);

  if (isLoading) {
    return (
      <div className="loader-container">
        <CircularProgress />
      </div>
    );
  }

  if (showSubmittedPage) {
    return <FormSubmittedSplashPage formName="Group Implementation" />;
  }

  const steps = [
    {
      title: "Group Information",
      description:
        "This initial section allow us to obtain group information in order to generate agreements and set up the group in our claims and billing software. All questions require an answer. If you are not sure what to enter you can always save the online form and return to complete the form when have the necessary information.",
      schema: groupInfoSchema,
      uiSchema: dynamicGiUiSchema,
    },
    {
      title: "COBRA Administration",
      description:
        "OptiMedHealth provides COBRA Administrative services for all of our plans sold, at no cost to the group. Some groups however have their administrative services for processing COBRA and want to handle all of the COBRA administrative services exclusively with their current partner. Please confirm how you would like to handle the COBRA administrative services for the OptiMedHealth Plans.",
      schema: cobraAdminSchema,
      uiSchema: dynamicCaUiSchema,
    },
    {
      title: "Products Sold",
      description: "This section is where you select all your sold products.",
      schema: productsSoldSchema,
      uiSchema: dynamicPsUiSchema,
    },
    {
      title: "Enrollment",
      description:
        "This next section will ask questions regarding how you would like enrollments processed, as well as the plan design(s) the group has elected. \n\nIMPORTANT: The group will have 3 options for enrollment submission and management to OptiMedHealth. Once you determine what the group will choose please complete the enrollment information below.  ",
      schema: enrollmentSchema,
      uiSchema: dynamicEpUiSchema,
    },
    {
      title: "Document Submission",
      description:
        "Please complete this section by adding the appropriate documents in order to have the group setup and underwriting process completed. As a reminder you can save this online submission and return at anytime to submit documents. You may also submit documents as you received them from your client.",
      schema: docUploadSchema,
      uiSchema: docUploadUiSchema,
    },
  ];

  const progress = (page / (steps.length - 1)) * 100;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            backgroundColor: "#ffffff",
            borderRadius: 2,
            border: "2px solid #000",
            boxShadow: "none",
            overflow: "hidden",
          }}
        >
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <img
              src={img}
              alt="OptiMed Health"
              style={{
                maxWidth: "350px",
                height: "auto",
                display: "block",
                margin: "0 auto",
              }}
            />
          </Box>
          {!showForm ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography sx={{ mb: 4 }}>
                Congratulations on your sold OptiMedHealth Plan! In order to begin the group's implementation we need to
                collect important information about the group. This online form is commonly referred to as "Group
                Implementation Form", or GIF. By completing this online form our team will be able to generate the
                necessary documents for you and the employer. We utilize DocuSign to electronically sign and return the
                documents necessary to complete the implementation. IMPORTANT: This online form does not have to be
                completed at once. If you need to collect further information and/or documentation you can save the
                document and return to it. How do I get started? Click next to begin and start with entering the group
                number provided in your email. Please note that all questions are required to be answered. If there are
                questions that do not apply to your group please enter "N/A". If you do not have the group number you
                can contact your account manager to obtain it. If you have any questions about how to complete the
                online form, please contact your account manager at am@optimedhealth.com.
              </Typography>

              <Button
                variant="contained"
                onClick={() => setShowForm(true)}
                sx={{
                  backgroundColor: "#5a6c7d",
                  color: "white",
                  px: 4,
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: "150px",
                  "&:hover": {
                    backgroundColor: "#4a5c6d",
                  },
                }}
              >
                Start
              </Button>
            </Box>
          ) : (
            <>
              {/* header */}
              <Box
                sx={{
                  backgroundColor: "#1a365d",
                  color: "white",
                  p: 3,
                  mb: 3,
                  textAlign: "center",
                  borderRadius: 2,
                }}
              >
                <Typography variant="h5">{steps[page]?.title}</Typography>

                <Typography variant="body1" sx={{ color: "white", mt: 3, whiteSpace: "pre-line" }}>
                  {steps[page]?.description}
                </Typography>
              </Box>

              {/* form */}
              <Form
                schema={steps[page]?.schema}
                uiSchema={steps[page]?.uiSchema}
                formData={formData}
                validator={validator}
                widgets={widgets}
                templates={templates}
                onChange={handleChange}
                onSubmit={page === steps.length - 1 ? handleSubmit : () => setPage((p) => p + 1)}
                onError={handleError}
                showErrorList="top"
                className={styles["custom-form"]}
              >
                {/* Doc upload */}
                {page === steps.length - 1 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 3, textAlign: "center", fontSize: "18px" }}>
                      Please use the following link to upload all implementation documents:
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "center", fontSize: "24px" }}>
                      <a href="https://www.optimedhealthportal.com/upload" target="_blank" rel="noopener noreferrer">
                        Document Uploads
                      </a>
                    </Typography>
                  </Box>
                )}
                <Button
                  variant="contained"
                  onClick={() => handleOenSaveAndResume()}
                  sx={{
                    backgroundColor: "#5a6c7d",
                    color: "white",
                    px: 3,
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    minWidth: "150px",
                    border: "none",
                    "&:hover": {
                      backgroundColor: "#4a5c6d",
                      border: "none",
                    },
                  }}
                >
                  Save and Resume Later
                </Button>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mt: 3,
                    width: "100%",
                  }}
                >
                  {/* left */}
                  <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-start" }}>
                    <Button
                      variant="contained"
                      disabled={page === 0}
                      onClick={() => setPage((p) => p - 1)}
                      sx={{
                        backgroundColor: "#5a6c7d",
                        color: "white",
                        px: 3,
                        py: 1.2,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        minWidth: "150px",
                        border: "none",
                        "&:hover": {
                          backgroundColor: "#4a5c6d",
                          border: "none",
                        },
                      }}
                    >
                      Back
                    </Button>
                  </Box>

                  {/* center */}
                  {/* Page indicator with progress bar - center */}
                  <Box
                    sx={{
                      flex: "1 1 auto",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      px: 2,
                      pb: 0,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 0.5, whiteSpace: "nowrap", fontSize: "0.875rem" }}
                    >
                      {page + 2} of {steps.length + 1}
                    </Typography>
                    {/* Progress bar */}
                    <Box
                      sx={{
                        width: "200px",
                        height: "4px",
                        backgroundColor: "#d3d3d3",
                        borderRadius: "2px",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          width: `${progress}%`,
                          height: "100%",
                          backgroundColor: "#808080",
                          transition: "width 0.3s ease",
                        }}
                      />
                    </Box>
                  </Box>

                  {/* right */}
                  <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      type="submit"
                      sx={{
                        backgroundColor: "#5a6c7d",
                        color: "white",
                        px: 3,
                        py: 1.2,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        minWidth: "150px",
                        border: "none",
                        "&:hover": {
                          backgroundColor: "#4a5c6d",
                          border: "none",
                        },
                      }}
                    >
                      {page === steps.length - 1 ? "Submit" : "Next"}
                    </Button>
                  </Box>
                </Box>
              </Form>

              {/* footer */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="caption">* Required fields must be completed</Typography>
              </Box>
            </>
          )}
        </Paper>
      </Container>
      <SaveAndResumeDialog
        open={openSaveAndResume}
        onClose={handleCloseSaveAndResume}
        formNumber={formNumber}
        strippedFormData={strippedFormData}
      ></SaveAndResumeDialog>
      <ConfirmResumeOTCDialog
        open={openConfirmResumeOTC}
        onClose={handleCloseConfirmResumeOTC}
        guid={guid}
        setFormData={setFormData}
      />
    </ThemeProvider>
  );
};

export default GroupImplementationForm;
